import {
  ApiBlueprint,
  PageBlueprint,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import LanguageIcon from '@material-ui/icons/Language';
import { Navigate, useLocation } from 'react-router-dom';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { environmentManagerAdminPermission } from './permissions';
import {
  environmentsRouteRef,
  environmentRouteRef,
  legacyEnvironmentsRedirectRouteRef,
} from './routes';
import { ENVIRONMENTS_BASE_URL, LEGACY_ENVIRONMENTS_BASE_URL } from './const';
import { configsApiRef, environmentsApiRef } from './types';
import { EnvironmentsRestApi, ConfigsRestApi } from './apis';
import { backendAuthApiRef } from '@internal/plugin-neo4j-auth-common';

// ── APIs (→ global API registry) ────────────────────────
const environmentsApi = ApiBlueprint.make({
  name: 'environments-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: environmentsApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
          auth: backendAuthApiRef,
        },
        factory: ({ discoveryApi, fetchApi, auth }) =>
          new EnvironmentsRestApi({ discoveryApi, fetchApi, auth }),
      }),
    ),
});

const configsApi = ApiBlueprint.make({
  name: 'configs-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: configsApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
          auth: backendAuthApiRef,
        },
        factory: ({ discoveryApi, fetchApi, auth }) =>
          new ConfigsRestApi({ discoveryApi, fetchApi, auth }),
      }),
    ),
});

// ── Route (→ app/routes) ────────────────────────────────
const environmentManagerPage = PageBlueprint.make({
  params: {
    title: 'Environments',
    icon: <LanguageIcon fontSize="inherit" />,
    path: ENVIRONMENTS_BASE_URL,
    routeRef: environmentsRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/EnvironmentsRoot').then(m => (
        <RequirePermission permission={environmentManagerAdminPermission}>
          <m.EnvironmentsRoot />
        </RequirePermission>
      )),
  },
});

// Redirect the legacy `/environments-v3` URL to the same sub-path under
// `/environments`, so that old links and bookmarks keep working. The omni CLI
// prints links to single environments and configs.
const legacyEnvironmentsRedirectPage = PageBlueprint.make({
  name: 'environments-legacy-redirect',
  params: {
    path: LEGACY_ENVIRONMENTS_BASE_URL,
    routeRef: legacyEnvironmentsRedirectRouteRef,
    loader: async () => <LegacyEnvironmentsRedirect />,
  },
});

export default createFrontendPlugin({
  pluginId: 'neo4j-environment-manager',
  extensions: [
    environmentsApi,
    configsApi,
    environmentManagerPage,
    legacyEnvironmentsRedirectPage,
  ],
  info: {
    packageJson: () => import('../package.json'),
    manifest: () => import('../catalog-info.yaml'),
  },
  routes: {
    root: environmentsRouteRef,
    environment: environmentRouteRef,
  },
});

// `replace` keeps the legacy URL out of the history.
function LegacyEnvironmentsRedirect() {
  const { pathname, search, hash } = useLocation();
  const subPath = pathname.slice(LEGACY_ENVIRONMENTS_BASE_URL.length);

  return (
    <Navigate
      to={`${ENVIRONMENTS_BASE_URL}${subPath}${search}${hash}`}
      replace
    />
  );
}
